import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiFetch, localLogout } from '../api';
import useActivePing from '../hooks/useActivePing';
import useSessionKeepAlive from '../hooks/useSessionKeepAlive';
import { msalInstance, loginRequest } from '../msalConfig';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  // Who may make decisions is NordComply's choice per account; the server enforces it, this only hides what would be refused.
  const [canDecide, setCanDecide] = useState(true);
  // NordComply's own team: the owner, and the permissions (news, licenses, delete) the owner has given.
  const [permissions, setPermissions] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [tenantIds, setTenantIds] = useState([]);
  const [orgName, setOrgName] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  // via: interactive (the person pressed Log ind), renewed (Microsoft signed them in again by itself) or restored (the page opened with
  // a sign-in that was still good). The server writes it in the activity log.
  const fetchRole = async (via = 'restored') => {
    setRoleLoading(true);
    try {
      const res = await apiFetch(`/api/me?via=${via}`);
      if (res.ok) {
        const data = await res.json();
        setRole(data.role || null);
        setCanDecide(data.canDecide !== false);
        setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
        setIsOwner(!!data.isOwner);
        setTenantIds(data.tenantIds || []);
        setOrgName(data.orgName || null);
      } else {
        // Any non-ok response — 401 (token invalid/expired, or no matching
        // user record) or anything else — must clear the session, not just
        // leave role stuck at null with user still set. Without this, a
        // signed-in-with-Microsoft-but-unregistered user hits ProtectedRoute
        // with a truthy user + null role, which redirected back into the
        // same protected route it just failed to pass: a blank-page loop,
        // not the "you're not registered" message it should show.
        setUser(null);
        localStorage.removeItem('nordcomply_user');
        const body = res.status === 403 ? await res.json().catch(() => ({})) : {};
        setAuthError(
          res.status === 401
            ? 'Denne bruger er ikke registreret i NordComply. Kontakt din administrator.'
            : body.code === 'account_locked'
            ? 'Din konto er låst. Kontakt din administrator.'
            : body.code === 'pending_approval'
            ? 'Din konto afventer godkendelse. Du får adgang, når en administrator har aktiveret den.'
            : 'Kunne ikke bekræfte login. Prøv igen.'
        );
      }
    } catch (err) {
      console.error('Error fetching /api/me:', err);
      setUser(null);
      localStorage.removeItem('nordcomply_user');
      setAuthError('Kunne ikke bekræfte login. Prøv igen.');
    } finally {
      setRoleLoading(false);
    }
  };

  const completeLogin = (result, via = 'interactive') => {
    const userData = {
      id: result.account.homeAccountId,
      email: result.account.username,
      name: result.account.name,
      token: result.accessToken,
    };
    setUser(userData);
    localStorage.setItem('nordcomply_user', JSON.stringify(userData));
    fetchRole(via);
  };

  // Local (email/password + TOTP) login/setup already returns role +
  // tenantIds directly in its response — no need for the extra /api/me
  // round-trip completeLogin above relies on for the Microsoft path.
  const completeLocalLogin = (result) => {
    const userData = {
      id: result.user.email,
      email: result.user.email,
      name: result.user.name,
      token: result.token,
      authMethod: 'local',
    };
    setUser(userData);
    localStorage.setItem('nordcomply_user', JSON.stringify(userData));
    setRole(result.user.role || null);
    setTenantIds(result.user.tenantIds || []);
    setRoleLoading(false);
  };

  useEffect(() => {
    async function init() {
      await msalInstance.initialize();

      // Returning from a Microsoft login redirect? This resolves with the
      // auth result and completes the login. MSAL needs its own
      // sessionStorage bookkeeping intact to recognize this, so nothing
      // here touches sessionStorage before this call.
      const redirectResult = await msalInstance.handleRedirectPromise().catch((err) => {
        console.error('MSAL redirect error:', err);
        setAuthError(err?.errorCode || 'Login fejlede under redirect.');
        return null;
      });

      if (redirectResult) {
        setAuthError('');
        // A sign-in the app itself asked Microsoft for a moment ago (api.js, reauthenticate) is a renewal, not a login the person made.
        let renewed = false;
        try { renewed = Date.now() - Number(sessionStorage.getItem('nc-reauth-at') || 0) < 2 * 60 * 1000; } catch { /* ignore */ }
        completeLogin(redirectResult, renewed ? 'renewed' : 'interactive');
      } else {
        const storedUser = localStorage.getItem('nordcomply_user');
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
            fetchRole();
          } catch (e) {
            localStorage.removeItem('nordcomply_user');
            setRoleLoading(false);
          }
        } else {
          setRoleLoading(false);
        }
      }
      setLoading(false);
    }

    init();
    // Mount-only by design (resolves the redirect / restores the session
    // exactly once) — completeLogin and fetchRole only close over stable
    // setState functions, so omitting them here is safe; adding them would
    // instead make this re-run on every render, since neither is memoized.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginWithMicrosoft = () => {
    setAuthError('');
    // Full-page redirect — the page navigates away and
    // handleRedirectPromise() above picks the result back up once
    // Microsoft sends the browser back.
    return msalInstance.loginRedirect(loginRequest);
  };

  const logout = () => {
    const wasLocal = user?.authMethod === 'local';
    if (wasLocal) {
      // Best-effort — the session row expires on its own (SESSION_TTL_MS)
      // even if this fails, so it never blocks clearing the local state.
      localLogout().catch(() => {});
    }
    setUser(null);
    setRole(null);
    setTenantIds([]);
    setOrgName(null);
    setRoleLoading(false);
    localStorage.removeItem('nordcomply_user');
    // A local-auth user never went through Microsoft, so there's no MSAL
    // session to redirect through — just send them back to /login directly.
    if (wasLocal) window.location.href = '/login';
    else msalInstance.logoutRedirect().catch(() => {});
  };

  // Active time is counted for anyone signed in, whatever their role; only the owner is shown it.
  useActivePing(!!user && !!role);
  // A Microsoft sign-in is kept fresh while the person works (see the hook).
  useSessionKeepAlive(!!user && !!role);

  return (
    <AuthContext.Provider value={{ user, loading, role, canDecide, permissions, isOwner, tenantIds, orgName, roleLoading, authError, loginWithMicrosoft, completeLocalLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// Customers land directly on their own tenant (no tenant list) — MSP admins
// land on the multi-tenant list — providers (NordComply) land on the
// organizations list, one tier up from that.
export function getHomePath(role, tenantIds) {
  if (role === 'customer') {
    return tenantIds && tenantIds.length > 0 ? `/tenant/${tenantIds[0]}` : '/tenants';
  }
  if (role === 'provider') {
    return '/organizations';
  }
  return '/tenants';
}
